import { useFocusEffect, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Home as HomeIcon, RefreshCw, Sparkles } from 'lucide-react-native';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View, type ViewStyle } from 'react-native';
import { useConfetti } from '@/components/ConfettiOverlay';
import { markBootReady } from '@/lib/boot-ready';
import { registerScrollToTop } from '@/lib/scroll-bus';
import { CalendarPeek } from '@/components/home/CalendarPeek';
import { GoalsPeek } from '@/components/home/GoalsPeek';
import { hapticSuccess } from '@/lib/haptics';
import { Greeting } from '@/components/home/Greeting';
import { WhyPopover } from '@/components/home/WhyPopover';
import { HeroCard, HeroEmpty } from '@/components/home/HeroCard';
import { ReschedulePicker } from '@/components/home/ReschedulePicker';
import { SectionLabel } from '@/components/home/SectionLabel';
import { CompactTaskRow } from '@/components/home/CompactTaskRow';
import { TaskRow } from '@/components/home/TaskRow';
import { WeatherPanel } from '@/components/home/WeatherPanel';
import { Fonts, Palette, Spacing } from '@/constants/theme';
import { useHomeData } from '@/hooks/use-home-data';
import { cancelTaskReminder } from '@/lib/notifications';
import { getActionByKey, parseToolbarKeys } from '@/lib/quick-toolbar';
import {
  completeTask,
  deleteTask,
  dueLabel,
  hardDeleteTask,
  restoreHardDeletedTask,
  setTaskDueAt,
  skipTask,
  snoozeTask,
  uncompleteTask,
  undeleteTask,
  type ActiveTask,
  type ReplanDiff,
} from '@/lib/tasks';
import { ReplanSummaryModal } from '@/components/ReplanSummaryModal';
import { TaskActionsSheet } from '@/components/TaskActionsSheet';
import { useConfirm } from '@/components/ConfirmDialog';
import { bumpDataRevision } from '@/lib/data-revision';
import { offerUndo } from '@/lib/undo';
import type { ComponentType } from 'react';

export default function ThisWeek() {
  const router = useRouter();
  const db = useSQLiteContext();
  const {
    loading, property, tasks, partition, doneThisWeek, ringPct, season, streak,
    systemsToTune, attributesBlank, firstBlankLabel, firstBlankField, homeCount, suggestReplan, refresh,
  } = useHomeData();
  const { fire } = useConfetti();
  // Replan preview modal — opens when user taps the Replan icon in
  // the Today submenu. Same preview+confirm flow as My Home / Edit
  // Home / Tools / Settings.
  const [replanSummary, setReplanSummary] = useState<ReplanDiff | null>(null);
  const [replanLoading, setReplanLoading] = useState(false);
  const [reschedulingTask, setReschedulingTask] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [whyTask, setWhyTask] = useState<ActiveTask | null>(null);
  // Context-menu target (long-press / right-click on a task row).
  const [menuTask, setMenuTask] = useState<ActiveTask | null>(null);
  const confirm = useConfirm();

  // Scroll-to-top affordances. Each tab manages its own ScrollView ref;
  // useFocusEffect resets scroll on tab switch (so the user doesn't
  // land mid-page on the previous scroll position), and the scroll bus
  // wires the same reset to the AppHeader logo so tapping it scrolls
  // home to top even when the user is already on home.
  const homeScrollRef = useRef<ScrollView | null>(null);
  const resetScroll = useCallback(() => {
    homeScrollRef.current?.scrollTo({ y: 0, animated: false });
  }, []);
  useFocusEffect(useCallback(() => {
    resetScroll();
  }, [resetScroll]));
  useEffect(() => registerScrollToTop(resetScroll), [resetScroll]);

  const showWhy = useCallback((task: ActiveTask) => setWhyTask(task), []);

  const onComplete = useCallback(
    async (taskId: string, point: { x: number; y: number }, size: number) => {
      fire(point.x, point.y, size);
      hapticSuccess();
      await completeTask(db, taskId);
      // OS reminder would still ping you about a task you finished — stop it.
      await cancelTaskReminder(taskId);
      await refresh();
      // Fat-finger escape hatch: 5 seconds to reverse an accidental tap.
      offerUndo({
        message: 'Task marked done',
        undo: () => uncompleteTask(db, taskId),
        onUndone: () => void refresh(),
      });
    },
    [db, refresh, fire]
  );

  // Skip closes the task without celebrating it — no confetti. Same DB
  // mechanics as complete (closes the task, schedules the next occurrence
  // for recurring), but tagged kind='skip' so the past-tasks view can
  // separate "I did it" from "I moved past it."
  const onSkip = useCallback(
    async (taskId: string) => {
      await skipTask(db, taskId);
      await cancelTaskReminder(taskId);
      await refresh();
      offerUndo({
        message: 'Task skipped',
        undo: () => uncompleteTask(db, taskId),
        onUndone: () => void refresh(),
      });
    },
    [db, refresh]
  );

  // Context-menu deletes. Soft delete is undoable; permanent delete
  // confirms loudly (it takes the completion history with it).
  const onMenuDelete = useCallback(
    async (task: ActiveTask) => {
      await deleteTask(db, task.id);
      await refresh();
      offerUndo({
        message: 'Task deleted',
        undo: () => undeleteTask(db, task.id),
        onUndone: () => void refresh(),
      });
    },
    [db, refresh]
  );

  const onMenuDeleteForever = useCallback(
    async (task: ActiveTask) => {
      const ok = await confirm({
        title: 'Delete forever?',
        message:
          `"${task.title}" and its entire completion history will be ` +
          `permanently removed — costs logged on it disappear from ` +
          `service history too. This cannot be undone.`,
        destructive: true,
        confirmLabel: 'Delete forever',
      });
      if (!ok) return;
      const snap = await hardDeleteTask(db, task.id);
      await refresh();
      // Short-lived escape hatch: the snapshot lives only in this
      // toast's 5-second memory window — after that it's truly gone.
      if (snap) {
        offerUndo({
          message: 'Task permanently deleted',
          undo: () => restoreHardDeletedTask(db, snap),
          onUndone: () => void refresh(),
        });
      }
    },
    [db, refresh, confirm]
  );

  const onSnooze = useCallback(
    (taskId: string, title: string) => {
      // Opens the Reschedule picker (replaces the silent 7-day snooze).
      setReschedulingTask({ id: taskId, title });
    },
    []
  );

  const confirmReschedule = useCallback(
    async (days: number) => {
      if (!reschedulingTask) return;
      const taskId = reschedulingTask.id;
      const { prevDueAt } = await snoozeTask(db, taskId, days);
      // Old reminder is now stale — clear it. User can set a fresh one
      // from the task detail screen if they want.
      await cancelTaskReminder(taskId);
      setReschedulingTask(null);
      await refresh();
      offerUndo({
        message: 'Task rescheduled',
        undo: () => setTaskDueAt(db, taskId, prevDueAt),
        onUndone: () => void refresh(),
      });
    },
    [db, refresh, reschedulingTask]
  );

  const snoozeTomorrow = useCallback(
    async (taskId: string) => {
      const { prevDueAt } = await snoozeTask(db, taskId, 1);
      await cancelTaskReminder(taskId);
      await refresh();
      offerUndo({
        message: 'Moved to tomorrow',
        undo: () => setTaskDueAt(db, taskId, prevDueAt),
        onUndone: () => void refresh(),
      });
    },
    [db, refresh]
  );

  // Signal to the root layout that the home tab has finished its first
  // fetch — _layout's Preloader stays mounted until this flips, so the
  // user sees ONE continuous animation through the entire boot instead
  // of a mount/unmount jitter between phases.
  useEffect(() => {
    if (!loading) markBootReady();
  }, [loading]);

  // No local Preloader: the root layout renders one global overlay that
  // covers the whole app while loading, then fades out when this tab
  // (or any tab) signals boot-ready. We just render a quiet background
  // here so the layout doesn't shift under the overlay.
  if (loading) {
    return <View style={styles.container} />;
  }

  if (!property) {
    return (
      <View style={[styles.container, styles.empty]}>
        <Text style={styles.emptyTitle}>No property yet</Text>
        <Text style={styles.emptyBody}>
          Restart the app to begin onboarding — Settings → Reset is the fastest path.
        </Text>
      </View>
    );
  }

  const { hero, thisWeek, upcoming } = partition;
  const moreCount = thisWeek.length;
  const nextLabel = thisWeek[0]?.title ?? upcoming[0]?.title;

  return (
    <View style={styles.container}>
      <ScrollView
        ref={homeScrollRef}
        contentContainerStyle={{ paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}>
        <Greeting
          doneThisWeek={doneThisWeek}
          ringPct={ringPct}
          backgroundUri={property?.background_uri ?? null}
          homeName={property?.name ?? null}
          ownerFirstName={property?.owner_first_name ?? null}
          greetingSource={property?.greeting_source ?? 'first_name'}
          seasonPct={season.pct}
          seasonName={season.season}
          streak={streak}
          systemsToTune={systemsToTune}
          attributesBlank={attributesBlank}
          firstBlankLabel={firstBlankLabel}
          firstBlankField={firstBlankField}
          homeCount={homeCount}
          suggestReplan={suggestReplan}
          topRightActions={
            <>
              {/* Edit removed (Wave 59, user call): the Homes manager
                  and My Home both reach the editor — three entry
                  points was clutter. */}
              <TodayIconBtn
                Icon={HomeIcon}
                label="Homes"
                accessibilityLabel="Open Home Manager"
                onPress={() => router.push('/homes' as never)}
              />
              <TodayIconBtn
                Icon={Sparkles}
                label="Tune"
                accessibilityLabel="Tune my plan"
                // Honey + badge when there's something to tune;
                // calm forest otherwise. Same logic as My Home.
                tone={(systemsToTune + attributesBlank) > 0 ? 'honey' : 'forest'}
                badge={systemsToTune + attributesBlank}
                onPress={() => router.push('/profile-setup' as never)}
              />
              <TodayIconBtn
                Icon={RefreshCw}
                label="Replan"
                accessibilityLabel={
                  property?.needs_replan === 1
                    ? 'Replan year — changes pending'
                    : 'Refresh plan'
                }
                // Honey + badge when the property has been marked
                // needs_replan (a planner-affecting field changed
                // since the last regen). Mirrors My Home so the
                // signal is identical across tabs.
                tone={property?.needs_replan === 1 ? 'honey' : 'forest'}
                badge={property?.needs_replan === 1 ? 1 : undefined}
                onPress={async () => {
                  if (!property) return;
                  setReplanLoading(true);
                  setReplanSummary({
                    added: 0, removed: 0, rescheduled: 0,
                    addedTitles: [], removedTitles: [], rescheduledTitles: [],
                  });
                  const { previewReplanDiff } = await import('@/lib/tasks');
                  const diff = await previewReplanDiff(db, property.id);
                  setReplanSummary(diff);
                  setReplanLoading(false);
                }}
              />
            </>
          }
        />

        {/* Quick toolbar mirrored from My Home — gives the user one-tap
            access to their favorite actions from the moment they open the
            app, before they scroll past weather and tasks. Same key list as
            My Home's row, edited in Settings → Manage My Home toolbar.
            Hidden via Settings → "Show quick toolbar on Today" toggle for
            users who want a calmer landing page. */}
        {property && (property.show_quick_toolbar ?? 1) === 1 ? (
          <HomeToolbar
            keys={parseToolbarKeys(property.quick_toolbar_keys_json)}
            onPick={(route) => router.push(route as never)}
          />
        ) : null}

        {(property?.show_weather ?? 1) === 1 ? (
          <WeatherPanel
            zip={property?.zip ?? null}
            lat={property?.lat ?? null}
            lon={property?.lon ?? null}
          />
        ) : null}

        {(property?.show_daily_calendar ?? 1) === 1 ? (
          <CalendarPeek tasks={tasks} />
        ) : null}

        {(property?.show_projects_on_today ?? 1) === 1 ? <GoalsPeek /> : null}

        <SectionLabel
          title="Your one thing"
          hint={hero ? dueLabel(hero.due_at) : 'Nice work'}
        />

        <View style={{ paddingHorizontal: Spacing.four }}>
          {hero ? (
            <HeroCard
              task={hero}
              onComplete={(p) => onComplete(hero.id, p, 16)}
              onSkip={() => onSkip(hero.id)}
              onSnooze={() => onSnooze(hero.id, hero.title)}
              onSnoozeTomorrow={() => snoozeTomorrow(hero.id)}
            />
          ) : (
            <HeroEmpty next={nextLabel} />
          )}
        </View>

        {hero && moreCount > 0 ? (
          <Pressable
            onPress={() => router.push('/(tabs)/schedule' as never)}
            style={({ pressed }) => [styles.strip, pressed && { opacity: 0.85 }]}>
            <Text style={styles.stripText}>
              <Text style={styles.stripBold}>{moreCount}</Text> more this week
            </Text>
            <Text style={styles.stripGo}>View all ›</Text>
          </Pressable>
        ) : null}

        {thisWeek.length > 0 ? (
          <>
            <SectionLabel title="The rest of this week" />
            <View style={styles.compactList}>
              {thisWeek.map((t) => (
                <CompactTaskRow
                  key={t.id}
                  task={t}
                  onComplete={(p) => onComplete(t.id, p, 9)}
                  onSkip={() => onSkip(t.id)}
                  onReschedule={() => onSnooze(t.id, t.title)}
                  onWhy={showWhy}
                  onMenu={setMenuTask}
                />
              ))}
            </View>
          </>
        ) : null}

        <SectionLabel title="Coming up" hint="On the horizon" />
        {upcoming.length > 0 ? (
          <View style={styles.compactList}>
            {upcoming.map((t) => (
              <CompactTaskRow
                key={t.id}
                task={t}
                onComplete={(p) => onComplete(t.id, p, 9)}
                onSkip={() => onSkip(t.id)}
                onReschedule={() => onSnooze(t.id, t.title)}
                onWhy={showWhy}
                onMenu={setMenuTask}
              />
            ))}
          </View>
        ) : (
          <View style={styles.upcomingEmpty}>
            <Text style={styles.upcomingEmptyText}>
              Nothing scheduled in the next two months. Reschedule a task and it
              will land here.
            </Text>
          </View>
        )}
      </ScrollView>

      <TaskActionsSheet
        task={menuTask}
        onClose={() => setMenuTask(null)}
        onOpen={() => menuTask && router.push(`/task/${menuTask.id}` as never)}
        onComplete={() =>
          menuTask && onComplete(menuTask.id, { x: 200, y: 300 }, 9)
        }
        onSkip={() => menuTask && onSkip(menuTask.id)}
        onReschedule={() => menuTask && onSnooze(menuTask.id, menuTask.title)}
        onDelete={() => menuTask && onMenuDelete(menuTask)}
        onDeleteForever={() => menuTask && onMenuDeleteForever(menuTask)}
      />

      <ReplanSummaryModal
        visible={replanSummary !== null}
        diff={replanSummary}
        loading={replanLoading}
        homeName={property?.name?.trim() || 'this home'}
        onClose={() => {
          setReplanSummary(null);
          setReplanLoading(false);
        }}
        onConfirm={async () => {
          if (!property) return;
          try {
            const { regenerateTasksWithDiff } = await import('@/lib/tasks');
            await regenerateTasksWithDiff(db, property.id);
            bumpDataRevision();
          } finally {
            setReplanSummary(null);
            setReplanLoading(false);
          }
        }}
      />

      <ReschedulePicker
        task={reschedulingTask}
        onCancel={() => setReschedulingTask(null)}
        onPick={confirmReschedule}
      />

      <WhyPopover
        content={whyTask ? { title: whyTask.title, why: whyTask.why, tier: whyTask.tier } : null}
        onClose={() => setWhyTask(null)}
      />
    </View>
  );
}

/**
 * Horizontal toolbar wrapper that handles cross-platform scrolling.
 *
 * Native (iOS/Android): finger swipe just works — ScrollView's default.
 * Web: by default, vertical mouse-wheel doesn't translate to horizontal
 * scroll, so you can't reach hidden chips with a normal mouse. We attach
 * a wheel listener via the native DOM ref that converts wheel-Y → scroll-X.
 * Trackpad horizontal scroll keeps working alongside (deltaX is honored
 * unchanged); we only intervene when the user wheels vertically.
 */
function HomeToolbar({
  keys,
  onPick,
}: {
  keys: string[];
  onPick: (route: string) => void;
}) {
  // Web-only: attach a wheel handler so vertical mouse-wheel scrolls the
  // toolbar horizontally. On web the scroller is a plain CSS-overflow
  // View (Wave 61 — RN-web's ScrollView responder chain ate touch pans,
  // leaving mobile-web users unable to slide the row at all), so the ref
  // is the scrollable DOM element itself.
  const onWebRef = useCallback((node: View | null) => {
    if (Platform.OS !== 'web' || !node) return;
    const dom = node as unknown as HTMLElement;
    const handler = (e: WheelEvent) => {
      // Only convert vertical wheel; leave native horizontal alone.
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      // Page-down should still page-down the document if there's nowhere
      // left to scroll horizontally — let the event bubble in that case.
      const atRight = dom.scrollLeft + dom.clientWidth >= dom.scrollWidth - 1;
      const atLeft = dom.scrollLeft <= 0;
      if ((e.deltaY > 0 && atRight) || (e.deltaY < 0 && atLeft)) return;
      e.preventDefault();
      dom.scrollLeft += e.deltaY;
    };
    dom.addEventListener('wheel', handler, { passive: false });
  }, []);

  const buttons = keys.map((key) => {
    const a = getActionByKey(key);
    if (!a) return null;
    return (
      <HomeQuickBtn
        key={a.key}
        label={a.label}
        Icon={a.Icon}
        primary={a.primary}
        onPress={() => onPick(a.route)}
      />
    );
  });

  if (Platform.OS === 'web') {
    return (
      <View
        ref={onWebRef}
        style={{ overflow: 'scroll', scrollbarWidth: 'none' } as ViewStyle}>
        <View style={[{ flexDirection: 'row' }, styles.quickRow]}>{buttons}</View>
      </View>
    );
  }
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.quickRow}>
      {buttons}
    </ScrollView>
  );
}

/**
 * Compact toolbar button, twin of My Home's QuickBtn but tuned for the
 * home screen — smaller chip so the horizontal row doesn't compete with
 * the hero card visually.
 */
function HomeQuickBtn({
  label,
  Icon,
  onPress,
  primary,
}: {
  label: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  onPress: () => void;
  primary?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [styles.quickBtn, pressed && { transform: [{ scale: 0.95 }] }]}>
      <View style={[styles.quickIco, primary && { backgroundColor: Palette.forest }]}>
        <Icon size={17} color={primary ? Palette.cream : Palette.forest} strokeWidth={1.8} />
      </View>
      <Text style={styles.quickLabel} numberOfLines={1}>{label}</Text>
    </Pressable>
  );
}

/**
 * Today-page submenu icon — same icon+label stack pattern as the My
 * Home title icons and the Schedule action icons. Local duplicate
 * rather than a shared extract; will be hoisted to a shared component
 * if a fourth caller appears.
 */
function TodayIconBtn({
  Icon,
  label,
  accessibilityLabel,
  onPress,
  tone = 'forest',
  badge,
}: {
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  label: string;
  accessibilityLabel: string;
  onPress: () => void;
  /** Color theme — 'forest' (default green) or 'honey' (warm orange
   *  for attention states like an unresolved tune count). */
  tone?: 'forest' | 'honey';
  /** Small numeric badge in the top-right corner. Used by Tune to
   *  surface the count of attribute / subtype gaps. */
  badge?: number;
}) {
  const color = tone === 'honey' ? Palette.honeyDeep : Palette.forest;
  return (
    <Pressable
      onPress={onPress}
      accessibilityLabel={accessibilityLabel}
      hitSlop={6}
      style={({ pressed }) => [
        styles.titleIconStack,
        pressed && { transform: [{ scale: 0.94 }], opacity: 0.85 },
      ]}>
      <View style={styles.titleIconBtn}>
        <Icon size={17} color={color} strokeWidth={1.9} />
        {badge && badge > 0 ? (
          <View style={styles.titleIconBadge}>
            <Text style={styles.titleIconBadgeText}>
              {badge > 9 ? '9+' : String(badge)}
            </Text>
          </View>
        ) : null}
      </View>
      <Text style={styles.titleIconLabel} numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Palette.sand,
  },
  // Edit/Add submenu row beneath the Greeting. Matches My Home /
  // Schedule's icon+label stack pattern for cross-page consistency.
  titleIcons: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
  },
  titleIconStack: {
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 2,
  },
  titleIconBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Honey-tinted count badge in the top-right corner of an icon
  // button. Matches the My Home Tune badge so the same gap-count
  // signal appears in both places.
  titleIconBadge: {
    position: 'absolute',
    top: -3,
    right: -3,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    paddingHorizontal: 4,
    backgroundColor: Palette.honeyDeep,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: Palette.sand,
  },
  titleIconBadgeText: {
    fontFamily: Fonts.sansBold,
    fontSize: 10.5,
    color: Palette.cream,
    lineHeight: 11,
  },
  titleIconLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    lineHeight: 11,
    // Forest text on the cream pill — readable over any hero photo
    // AND consistent with the green-themed labels on My Home /
    // Schedule / Settings. The pill background is what carries the
    // contrast; the forest text gives the cross-page color match.
    color: Palette.forest,
    backgroundColor: 'rgba(241,232,216,0.92)',
    overflow: 'hidden',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    letterSpacing: 0.2,
    maxWidth: 70,
    textAlign: 'center',
  },
  quickRow: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.three,
    gap: 2,
  },
  quickBtn: {
    width: 60,
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 2,
    // Same as my-home — hard-pin width so horizontal ScrollView never
    // tries to fit everything by shrinking items.
    flexShrink: 0,
  },
  quickIco: {
    width: 46,
    height: 46,
    borderRadius: 14,
    backgroundColor: '#EAF0EA',
    borderWidth: 1,
    borderColor: Palette.line,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.ink,
    letterSpacing: 0.2,
    textAlign: 'center',
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  emptyTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 22,
    color: Palette.ink,
    marginBottom: 8,
  },
  emptyBody: {
    fontFamily: Fonts.sansMedium,
    fontSize: 14,
    color: Palette.inkSoft,
    textAlign: 'center',
    lineHeight: 21,
  },
  strip: {
    marginHorizontal: 30,
    paddingHorizontal: 16,
    paddingVertical: 11,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: Palette.line,
    borderBottomLeftRadius: 18,
    borderBottomRightRadius: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  stripText: {
    fontSize: 13.5,
    fontFamily: Fonts.sansSemibold,
    color: Palette.inkSoft,
  },
  stripBold: {
    color: Palette.ink,
    fontFamily: Fonts.sansBold,
  },
  stripGo: {
    fontSize: 13,
    fontFamily: Fonts.sansBold,
    color: Palette.forest,
  },
  list: {
    paddingHorizontal: 18,
    gap: 9,
  },
  // Compact list rhythm — tighter gap than the old TaskRow list because
  // CompactTaskRow is itself denser. The result is more rows per
  // viewport without feeling cramped (Todoist-style density target).
  compactList: {
    paddingHorizontal: 18,
    gap: 6,
  },
  upcoming: {
    paddingHorizontal: 18,
  },
  upcomingEmpty: {
    marginHorizontal: 18,
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Palette.line,
    borderStyle: 'dashed',
    backgroundColor: Palette.card,
  },
  upcomingEmptyText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    color: Palette.inkSoft,
    lineHeight: 18,
    textAlign: 'center',
  },
});
